import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type FontSizePreset = 'small' | 'medium' | 'large';

export interface AccessibilitySettings {
  fontSize: FontSizePreset;
  highContrast: boolean;
  highlightLinks: boolean;
  reduceMotion: boolean;
}

interface AccessibilityContextType {
  settings: AccessibilitySettings;
  updateSettings: (updates: Partial<AccessibilitySettings>) => void;
  resetSettings: () => void;
}

const DEFAULT_SETTINGS: AccessibilitySettings = {
  fontSize: 'medium',
  highContrast: false,
  highlightLinks: false,
  reduceMotion: false,
};

const STORAGE_KEY = 'stock_screener_accessibility';

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

interface AccessibilityProviderProps {
  children: React.ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    if (typeof window === 'undefined') return DEFAULT_SETTINGS;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
      }
    } catch {
      // Ignore parse errors
    }
    return DEFAULT_SETTINGS;
  });

  // Apply CSS classes to html element
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;

    // Map font size presets to percentage values
    const fontSizeMap: Record<FontSizePreset, string> = {
      small: '90%',
      medium: '100%',
      large: '115%',
    };
    root.style.setProperty('--user-font-size', fontSizeMap[settings.fontSize]);

    // Toggle classes
    root.classList.toggle('high-contrast', settings.highContrast);
    root.classList.toggle('highlight-links', settings.highlightLinks);
    root.classList.toggle('reduce-motion', settings.reduceMotion);

    // Also respect prefers-reduced-motion if reduceMotion is enabled
    if (settings.reduceMotion) {
      root.style.setProperty('--motion-duration', '0ms');
    } else {
      root.style.removeProperty('--motion-duration');
    }
  }, [settings]);

  // Persist to localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Ignore storage errors
    }
  }, [settings]);

  const updateSettings = useCallback((updates: Partial<AccessibilitySettings>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  return (
    <AccessibilityContext.Provider value={{ settings, updateSettings, resetSettings }}>
      {children}
    </AccessibilityContext.Provider>
  );
};
